import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

function toDateTimeLocal(isoOrNull: string | null | undefined): string {
  if (!isoOrNull) return '';
  const d = new Date(isoOrNull);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Campaign {
  id: string;
  nome: string;
  targetTags: string[];
  sessionNames: string[];
  messageType: string;
  messageContent: any;
  randomDelay: number;
  startImmediately: boolean;
  scheduledFor: string | null;
}

interface ContactTag {
  id: string;
  nome: string;
}

interface WhatsAppSession {
  name: string;
  displayName?: string;
  mePushName: string | null;
}

interface EditCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  campaign: Campaign | null;
  contactTags: ContactTag[];
  whatsappSessions: WhatsAppSession[];
}

type MessageContent = { sequence: Array<{ type: string; content: any }> };

export function EditCampaignModal({
  isOpen,
  onClose,
  onSuccess,
  campaign,
  contactTags,
  whatsappSessions,
}: EditCampaignModalProps) {
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Record<number, boolean>>({});
  const [fileInfos, setFileInfos] = useState<Record<number, { name: string; size: number; type: string }>>({});
  const [formData, setFormData] = useState({
    nome: '',
    targetTags: [] as string[],
    sessionNames: [] as string[],
    messageType: 'sequence' as string,
    messageContent: { sequence: [] as Array<{ type: string; content: any }> } as MessageContent,
    randomDelay: 30,
    startImmediately: true,
    scheduledFor: '',
  });

  useEffect(() => {
    if (campaign && isOpen) {
      const raw = campaign.messageContent;
      let sequence: Array<{ type: string; content: any }>;
      if (raw && typeof raw === 'object' && Array.isArray(raw.sequence)) {
        sequence = raw.sequence;
      } else if (raw && campaign.messageType && campaign.messageType !== 'sequence') {
        sequence = [{ type: campaign.messageType, content: raw }];
      } else {
        sequence = [];
      }
      // Normalizar cada item da sequência para os cards exibirem corretamente ao editar
      sequence = sequence.map((item) => {
        const content = item.content && typeof item.content === 'object' ? { ...item.content } : {};
        const type = item.type || 'text';

        if (type === 'text') {
          const hasVariations = Array.isArray(content.variations) && content.variations.length > 0;
          const useVariations = !!content.useVariations || hasVariations;
          if (useVariations && (!content.variations || content.variations.length === 0) && content.text) {
            content.variations = [content.text];
          }
          if (hasVariations) content.useVariations = true;
          return { ...item, type, content };
        }

        if (type === 'document' && !('fileName' in content) && content.url) {
          content.fileName = content.fileName ?? '';
        }
        if (['image', 'video', 'audio', 'document'].includes(type)) {
          content.url = content.url ?? '';
          if (type !== 'document') content.caption = content.caption ?? '';
        }
        if (type === 'wait') content.waitTime = content.waitTime ?? 30;
        if (type === 'openai' || type === 'groq') {
          content.model = content.model ?? '';
          content.system = content.system ?? '';
          content.user = content.user ?? '';
        }
        return { ...item, type, content };
      });
      setUploadingFiles({});
      setFileInfos({});
      setFormData({
        nome: campaign.nome,
        targetTags: campaign.targetTags || [],
        sessionNames: campaign.sessionNames || [],
        messageType: campaign.messageType || 'sequence',
        messageContent: { sequence },
        randomDelay: campaign.randomDelay ?? 30,
        startImmediately: campaign.startImmediately ?? true,
        scheduledFor: toDateTimeLocal(campaign.scheduledFor),
      });
    }
  }, [campaign, isOpen]);

  const handleTagToggle = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      targetTags: prev.targetTags.includes(tag) ? prev.targetTags.filter((t) => t !== tag) : [...prev.targetTags, tag],
    }));
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData((prev) => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter((s) => s !== sessionName)
        : [...prev.sessionNames, sessionName],
    }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    return '📄';
  };

  const handleFileUpload = async (file: File, messageIndex: number) => {
    setUploadingFiles((prev) => ({ ...prev, [messageIndex]: true }));
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};
      if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();
      setFileInfos((prev) => ({
        ...prev,
        [messageIndex]: {
          name: data.originalName ?? file.name,
          size: data.size ?? file.size,
          type: data.mimetype ?? file.type,
        },
      }));

      const seq = formData.messageContent?.sequence || [];
      const newSequence = seq.map((s: any, i: number) => {
        if (i !== messageIndex) return s;
        const content = { ...s.content, url: data.fileUrl };
        if (s.type === 'document') content.fileName = data.originalName ?? file.name;
        return { ...s, content };
      });
      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSequence } }));
      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [messageIndex]: false }));
    }
  };

  const handleRemoveFile = (messageIndex: number) => {
    const seq = formData.messageContent?.sequence || [];
    const newSequence = seq.map((s: any, i: number) =>
      i === messageIndex ? { ...s, content: { ...s.content, url: '' } } : s
    );
    setFormData((prev) => ({ ...prev, messageContent: { sequence: newSequence } }));
    setFileInfos((prev) => {
      const next = { ...prev };
      delete next[messageIndex];
      return next;
    });
    toast.success('Arquivo removido');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign) return;

    if (formData.targetTags.length === 0) {
      toast.error('Selecione pelo menos uma categoria de contato');
      return;
    }
    if (formData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma sessão WhatsApp');
      return;
    }
    // Permite salvar sem data de agendamento: campanha permanece PENDING até agendar
    const seq = formData.messageContent?.sequence || [];
    for (let i = 0; i < seq.length; i++) {
      const item = seq[i];
      if (item.type === 'text') {
        const hasText = item.content?.useVariations
          ? (item.content?.variations || []).some((v: string) => typeof v === 'string' && v.trim())
          : !!item.content?.text?.trim();
        if (!hasText) {
          toast.error(`Mensagem ${i + 1}: o texto não pode estar vazio`);
          return;
        }
      }
      if (['image', 'video', 'audio', 'document'].includes(item.type) && !item.content?.url?.trim()) {
        toast.error(`Mensagem ${i + 1}: a URL do arquivo não pode estar vazia`);
        return;
      }
    }

    try {
      setLoading(true);
      let finalMessageType = formData.messageType;
      let finalMessageContent: any = formData.messageContent;
      if (seq.length > 1) {
        finalMessageType = 'sequence';
        finalMessageContent = { sequence: seq };
      } else if (seq.length === 1) {
        finalMessageType = seq[0].type;
        finalMessageContent = seq[0].content;
      }
      const scheduledForISO = !formData.startImmediately && formData.scheduledFor
        ? new Date(formData.scheduledFor).toISOString()
        : null;
      await apiService.updateCampaign(campaign.id, {
        nome: formData.nome,
        targetTags: formData.targetTags,
        sessionNames: formData.sessionNames,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        randomDelay: formData.randomDelay,
        startImmediately: formData.startImmediately,
        scheduledFor: scheduledForISO,
      });
      toast.success('Campanha atualizada com sucesso');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar campanha');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !campaign) return null;

  const sequence = formData.messageContent?.sequence || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Editar Campanha</h2>
            <p className="text-sm text-gray-600 mt-1">Configure sua campanha de mensagens WhatsApp</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* COLUNA ESQUERDA - Informações Básicas */}
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="text-lg font-semibold text-blue-900 mb-2">Informações Básicas</h4>
                <p className="text-sm text-blue-700">Configure os dados fundamentais da sua campanha</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Campanha *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData((prev) => ({ ...prev, nome: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Promoção Black Friday 2024"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categorias de Contatos *</label>
                <p className="text-xs text-gray-500 mb-3">Selecione quais categorias de contatos receberão a campanha</p>
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                  {contactTags.map((tag) => (
                    <label key={tag.id} className="flex items-center space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.targetTags.includes(tag.id)}
                        onChange={() => handleTagToggle(tag.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 font-medium">{tag.nome}</span>
                    </label>
                  ))}
                  {contactTags.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria disponível</p>
                  )}
                </div>
                {formData.targetTags.length > 0 && (
                  <p className="text-xs text-green-600 mt-2">✅ {formData.targetTags.length} categoria(s) selecionada(s)</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Conexões WhatsApp * ({formData.sessionNames.length} selecionadas)</label>
                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-green-800">Multi-Sessão com Failover</h4>
                      <p className="text-sm text-green-700 mt-1">
                        Sistema inteligente que distribui envios entre múltiplas conexões com redundância automática.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                  {whatsappSessions.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">Nenhuma conexão WhatsApp disponível</p>
                  ) : (
                    whatsappSessions.map((session) => (
                      <label key={session.name} className="flex items-center justify-between space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.sessionNames.includes(session.name)}
                            onChange={() => handleSessionToggle(session.name)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">
                              {session.mePushName || session.displayName || session.name}
                            </span>
                            <span className="text-xs text-gray-500 block">({session.displayName || session.name})</span>
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">Ativa</span>
                      </label>
                    ))
                  )}
                </div>
                {formData.sessionNames.length > 0 && (
                  <p className="text-xs text-green-600 mt-2">✅ {formData.sessionNames.length} conexão(ões) selecionada(s)</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tempo de Randomização (segundos)</label>
                <input
                  type="number"
                  min={0}
                  max={300}
                  value={formData.randomDelay}
                  onChange={(e) => setFormData((prev) => ({ ...prev, randomDelay: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ⏱️ Intervalo aleatório entre envios (0-{formData.randomDelay}s) para evitar bloqueios
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quando Enviar</label>
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.startImmediately}
                      onChange={() => setFormData((prev) => ({ ...prev, startImmediately: true }))}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">🚀 Iniciar imediatamente</span>
                      <p className="text-xs text-gray-500">A campanha será executada assim que for salva</p>
                    </div>
                  </label>
                  <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      checked={!formData.startImmediately}
                      onChange={() => setFormData((prev) => ({ ...prev, startImmediately: false }))}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">📅 Agendar para:</span>
                      <p className="text-xs text-gray-500">Escolha data e hora específicas</p>
                    </div>
                  </label>
                  {!formData.startImmediately && (
                    <input
                      type="datetime-local"
                      value={formData.scheduledFor}
                      onChange={(e) => setFormData((prev) => ({ ...prev, scheduledFor: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ml-6"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* COLUNA DIREITA - Mensagens */}
            <div className="space-y-6">
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h4 className="text-lg font-semibold text-purple-900 mb-2">💬 Mensagens da Campanha</h4>
                <p className="text-sm text-purple-700">Configure o conteúdo que será enviado aos contatos</p>
              </div>

              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">💡 Variáveis Dinâmicas</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Personalize suas mensagens com:
                      <span className="block mt-1">
                        <code className="bg-blue-100 px-1 rounded mx-1">{'{{nome}}'}</code>
                        <code className="bg-blue-100 px-1 rounded mx-1">{'{{telefone}}'}</code>
                        <code className="bg-blue-100 px-1 rounded mx-1">{'{{email}}'}</code>
                        <code className="bg-blue-100 px-1 rounded mx-1">{'{{categoria}}'}</code>
                        <code className="bg-blue-100 px-1 rounded mx-1">{'{{observacoes}}'}</code>
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-600">Adicione uma ou mais mensagens para serem enviadas em sequência</p>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          messageContent: { sequence: [...sequence, { type: 'text', content: { text: '' } }] },
                        }))
                      }
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-2"
                    >
                      <span className="text-lg">+</span>
                      Mensagem
                    </button>
                  </div>
                  <div className="space-y-3">
                    {sequence.length === 0 && (
                      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <div className="text-4xl mb-4">📝</div>
                        <p className="text-lg font-medium">Nenhuma mensagem na sequência</p>
                        <p className="text-sm">Clique em &quot;+ Mensagem&quot; para adicionar mensagens à campanha.</p>
                      </div>
                    )}
                    {sequence.map((item: any, index: number) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 transition-all">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm font-medium text-gray-600">Mensagem {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newSeq = sequence.filter((_: any, i: number) => i !== index);
                              setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remover
                          </button>
                        </div>
                        <div className="space-y-3">
                          <select
                            value={item.type}
                            onChange={(e) => {
                              const newType = e.target.value;
                              let newContent: any;
                              switch (newType) {
                                case 'text':
                                  newContent = { text: '' };
                                  break;
                                case 'document':
                                  newContent = { url: '', fileName: '' };
                                  break;
                                case 'openai':
                                case 'groq':
                                  newContent = { model: '', system: '', user: '' };
                                  break;
                                case 'wait':
                                  newContent = { waitTime: 30 };
                                  break;
                                default:
                                  newContent = { url: '', caption: '' };
                                  break;
                              }
                              const newSeq = sequence.map((s: any, i: number) =>
                                i === index ? { type: newType, content: newContent } : s
                              );
                              setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="text">💬 Texto</option>
                            <option value="image">🖼️ Imagem</option>
                            <option value="video">🎬 Vídeo</option>
                            <option value="audio">🎵 Áudio</option>
                            <option value="document">📄 Arquivo</option>
                            <option value="openai">🤖 OpenAI</option>
                            <option value="groq">⚡ Groq AI</option>
                            <option value="wait">⏱️ Espera</option>
                          </select>

                            {/* TIPO: TEXTO - igual ao Nova Campanha */}
                            {item.type === 'text' && (
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id={`useVariations-edit-${index}`}
                                    checked={item.content?.useVariations || false}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) => {
                                        if (i !== index) return s;
                                        const checked = e.target.checked;
                                        return {
                                          ...s,
                                          content: {
                                            ...s.content,
                                            useVariations: checked,
                                            variations: checked ? (Array.isArray(s.content?.variations) && s.content.variations.length ? s.content.variations : [s.content?.text || '']) : undefined,
                                            text: checked ? undefined : (s.content?.text ?? (Array.isArray(s.content?.variations) ? s.content.variations[0] : ''))
                                          }
                                        };
                                      });
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="rounded text-blue-600 focus:ring-blue-500"
                                  />
                                  <label htmlFor={`useVariations-edit-${index}`} className="text-sm font-medium text-gray-700">
                                    Usar variações de texto
                                  </label>
                                </div>
                                {item.content?.useVariations ? (
                                  <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">Variações de texto</label>
                                    {(item.content?.variations || ['']).map((variation: string, varIndex: number) => (
                                      <div key={varIndex} className="flex gap-2">
                                        <div className="flex-1">
                                          <textarea
                                            placeholder={`Variação ${varIndex + 1}... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}`}
                                            value={variation}
                                            onChange={(e) => {
                                              const newSeq = sequence.map((s: any, i: number) => {
                                                if (i !== index) return s;
                                                const newVariations = [...(s.content?.variations || [])];
                                                while (newVariations.length <= varIndex) newVariations.push('');
                                                newVariations[varIndex] = e.target.value;
                                                return { ...s, content: { ...s.content, variations: newVariations } };
                                              });
                                              setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            rows={2}
                                          />
                                        </div>
                                        {(item.content?.variations || []).length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newSeq = sequence.map((s: any, i: number) => {
                                                if (i !== index) return s;
                                                const newVariations = (s.content?.variations || []).filter((_: string, j: number) => j !== varIndex);
                                                return { ...s, content: { ...s.content, variations: newVariations.length ? newVariations : [''] } };
                                              });
                                              setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                            }}
                                            className="px-2 py-1 text-red-600 hover:text-red-800"
                                            title="Remover variação"
                                          >
                                            ✕
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newSeq = sequence.map((s: any, i: number) =>
                                          i === index ? { ...s, content: { ...s.content, variations: [...(s.content?.variations || []), ''] } } : s
                                        );
                                        setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                      }}
                                      className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded hover:bg-blue-200 flex items-center gap-1"
                                    >
                                      <span>+</span>
                                      Nova variação
                                    </button>
                                  </div>
                                ) : (
                                  <div>
                                    <textarea
                                      placeholder="Digite sua mensagem... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                      value={item.content?.text ?? ''}
                                      onChange={(e) => {
                                        const newSeq = sequence.map((s: any, i: number) =>
                                          i === index ? { ...s, content: { ...s.content, text: e.target.value } } : s
                                        );
                                        setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      rows={3}
                                    />
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                  {['{{nome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                    <button
                                      key={variable}
                                      type="button"
                                      onClick={() => {
                                        const seq = formData.messageContent?.sequence || [];
                                        const current = seq[index]?.content;
                                        if (current?.useVariations) {
                                          const variations = current.variations || [''];
                                          const targetIndex = variations.findIndex((v: string) => !v.trim());
                                          const idx = targetIndex === -1 ? 0 : targetIndex;
                                          const newVariations = [...variations];
                                          while (newVariations.length <= idx) newVariations.push('');
                                          newVariations[idx] = (newVariations[idx] || '') + variable;
                                          const newSeq = sequence.map((s: any, i: number) =>
                                            i === index ? { ...s, content: { ...s.content, variations: newVariations } } : s
                                          );
                                          setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                        } else {
                                          const newSeq = sequence.map((s: any, i: number) =>
                                            i === index ? { ...s, content: { ...s.content, text: (s.content?.text || '') + variable } } : s
                                          );
                                          setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                        }
                                      }}
                                      className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                    >
                                      {variable}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* TIPO: ESPERA - card amarelo igual ao Nova Campanha */}
                            {item.type === 'wait' && (
                              <div className="space-y-3">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                  <div className="flex items-start gap-3">
                                    <span className="text-2xl">⏱️</span>
                                    <div className="flex-1">
                                      <h4 className="text-sm font-medium text-yellow-900 mb-2">Tempo de Espera</h4>
                                      <p className="text-xs text-yellow-700 mb-3">
                                        Aguarda um tempo antes de enviar a próxima mensagem
                                      </p>
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="number"
                                          min={1}
                                          max={3600}
                                          value={item.content?.waitTime ?? 30}
                                          onChange={(e) => {
                                            const newSeq = sequence.map((s: any, i: number) =>
                                              i === index ? { ...s, content: { waitTime: parseInt(e.target.value) || 30 } } : s
                                            );
                                            setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                          }}
                                          className="w-24 px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                                        />
                                        <span className="text-sm text-yellow-700">segundos</span>
                                      </div>
                                      <p className="text-xs text-yellow-600 mt-2">
                                        Mínimo: 1 segundo | Máximo: 3600 segundos (1 hora)
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* TIPO: OPENAI - card azul, modelo com sugestões, prompts, variáveis */}
                            {item.type === 'openai' && (
                              <div className="space-y-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-blue-600 font-medium">🤖 OpenAI</span>
                                  </div>
                                  <p className="text-sm text-blue-600">
                                    Configure os parâmetros para gerar mensagens personalizadas usando inteligência artificial
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo *</label>
                                  <input
                                    type="text"
                                    placeholder="Digite o modelo da OpenAI (ex: gpt-3.5-turbo)"
                                    value={item.content?.model ?? ''}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) =>
                                        i === index ? { ...s, content: { ...s.content, model: e.target.value } } : s
                                      );
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">Modelos sugeridos:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {['gpt-3.5-turbo', 'gpt-4', 'gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini'].map((model) => (
                                        <button
                                          key={model}
                                          type="button"
                                          onClick={() => {
                                            const newSeq = sequence.map((s: any, i: number) =>
                                              i === index ? { ...s, content: { ...s.content, model } } : s
                                            );
                                            setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                          }}
                                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 border border-gray-300"
                                        >
                                          {model}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Prompt do Sistema *</label>
                                  <textarea
                                    placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                    value={item.content?.system ?? ''}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) =>
                                        i === index ? { ...s, content: { ...s.content, system: e.target.value } } : s
                                      );
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows={3}
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Define como a IA deve se comportar e responder</p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Prompt do Usuário *</label>
                                  <textarea
                                    placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                    value={item.content?.user ?? ''}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) =>
                                        i === index ? { ...s, content: { ...s.content, user: e.target.value } } : s
                                      );
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows={4}
                                  />
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                    {['{{nome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                      <button
                                        key={variable}
                                        type="button"
                                        onClick={() => {
                                          const newSeq = sequence.map((s: any, i: number) =>
                                            i === index ? { ...s, content: { ...s.content, user: (s.content?.user || '') + variable } } : s
                                          );
                                          setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                        }}
                                        className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                      >
                                        {variable}
                                      </button>
                                    ))}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">A IA usará este prompt junto com os dados do contato para gerar a mensagem</p>
                                </div>
                              </div>
                            )}

                            {/* TIPO: GROQ - card amarelo, modelos Groq, mesma estrutura */}
                            {item.type === 'groq' && (
                              <div className="space-y-4">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-yellow-600 font-medium">⚡ Groq AI</span>
                                  </div>
                                  <p className="text-sm text-yellow-600">
                                    Configure os parâmetros para gerar mensagens personalizadas usando Groq AI com velocidade ultra-rápida
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo *</label>
                                  <input
                                    type="text"
                                    placeholder="Digite o modelo do Groq (ex: llama-3.1-8b-instant)"
                                    value={item.content?.model ?? ''}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) =>
                                        i === index ? { ...s, content: { ...s.content, model: e.target.value } } : s
                                      );
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">Modelos sugeridos:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {['llama-3.1-8b-instant', 'llama-3.1-70b-versatile', 'llama-3.2-11b-text-preview', 'mixtral-8x7b-32768', 'gemma2-9b-it'].map((model) => (
                                        <button
                                          key={model}
                                          type="button"
                                          onClick={() => {
                                            const newSeq = sequence.map((s: any, i: number) =>
                                              i === index ? { ...s, content: { ...s.content, model } } : s
                                            );
                                            setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                          }}
                                          className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded hover:bg-yellow-200 border border-yellow-300"
                                        >
                                          {model}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Prompt do Sistema *</label>
                                  <textarea
                                    placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                    value={item.content?.system ?? ''}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) =>
                                        i === index ? { ...s, content: { ...s.content, system: e.target.value } } : s
                                    );
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows={3}
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Define como a IA deve se comportar e responder</p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Prompt do Usuário *</label>
                                  <textarea
                                    placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                    value={item.content?.user ?? ''}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) =>
                                        i === index ? { ...s, content: { ...s.content, user: e.target.value } } : s
                                    );
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows={4}
                                  />
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                    {['{{nome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                      <button
                                        key={variable}
                                        type="button"
                                        onClick={() => {
                                          const newSeq = sequence.map((s: any, i: number) =>
                                            i === index ? { ...s, content: { ...s.content, user: (s.content?.user || '') + variable } } : s
                                          );
                                          setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                        }}
                                        className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded hover:bg-orange-200"
                                      >
                                        {variable}
                                      </button>
                                    ))}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">A IA usará este prompt junto com os dados do contato para gerar a mensagem</p>
                                </div>
                              </div>
                            )}

                            {/* TIPO: IMAGEM, VÍDEO, ÁUDIO, DOCUMENTO - upload igual ao modal Nova Campanha */}
                            {['image', 'video', 'audio', 'document'].includes(item.type) && (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <label className="block text-sm font-medium text-gray-700">Arquivo</label>
                                </div>
                                {!item.content?.url ? (
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="file"
                                      id={`edit-file-upload-${index}`}
                                      className="hidden"
                                      accept={
                                        item.type === 'image' ? 'image/*' :
                                        item.type === 'video' ? 'video/*' :
                                        item.type === 'audio' ? 'audio/*' :
                                        'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                      }
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleFileUpload(file, index);
                                        e.target.value = '';
                                      }}
                                      disabled={uploadingFiles[index]}
                                    />
                                    <label
                                      htmlFor={`edit-file-upload-${index}`}
                                      className={`flex-1 cursor-pointer px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg text-center text-sm font-medium transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                        uploadingFiles[index]
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                          : 'bg-white text-gray-700'
                                      }`}
                                    >
                                      {uploadingFiles[index] ? (
                                        <div className="flex flex-col items-center gap-2">
                                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                                          <span>Enviando arquivo...</span>
                                        </div>
                                      ) : (
                                        <div className="flex flex-col items-center gap-2">
                                          <div className="text-2xl">📁</div>
                                          <span>Clique para fazer upload do arquivo</span>
                                          <span className="text-xs text-gray-500">
                                            {item.type === 'image' && 'Imagens: JPG, PNG, GIF, WebP'}
                                            {item.type === 'video' && 'Vídeos: MP4, AVI, MOV, WMV, MKV'}
                                            {item.type === 'audio' && 'Áudios: MP3, WAV, OGG, AAC, M4A'}
                                            {item.type === 'document' && 'Documentos: PDF, DOC, XLS, TXT, ZIP'}
                                          </span>
                                        </div>
                                      )}
                                    </label>
                                  </div>
                                ) : (
                                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        {item.type === 'image' && (
                                          <img
                                            src={item.content.url}
                                            alt="Preview"
                                            className="w-12 h-12 object-cover rounded"
                                          />
                                        )}
                                        {item.type !== 'image' && (
                                          <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center text-2xl">
                                            {getFileIcon(fileInfos[index]?.type || item.type)}
                                          </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-gray-900 truncate">
                                            {fileInfos[index]?.name || item.content?.fileName || 'Arquivo carregado'}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {fileInfos[index]?.size ? formatFileSize(fileInfos[index].size) : ''}
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveFile(index)}
                                        className="text-red-600 hover:text-red-800 p-1"
                                        title="Remover arquivo"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {['image', 'video'].includes(item.type) && item.content?.url && (
                                  <div className="space-y-2">
                                    <input
                                      type="text"
                                      placeholder="Legenda (opcional) - Use variáveis como {{nome}}, {{telefone}}, etc."
                                      value={item.content?.caption ?? ''}
                                      onChange={(e) => {
                                        const newSeq = sequence.map((s: any, i: number) =>
                                          i === index ? { ...s, content: { ...s.content, caption: e.target.value } } : s
                                        );
                                        setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <div className="flex flex-wrap gap-1">
                                      <span className="text-xs text-gray-500">Variáveis:</span>
                                      {['{{nome}}', '{{telefone}}', '{{categoria}}'].map((variable) => (
                                        <button
                                          key={variable}
                                          type="button"
                                          onClick={() => {
                                            const newSeq = sequence.map((s: any, i: number) =>
                                              i === index ? { ...s, content: { ...s.content, caption: (s.content?.caption || '') + variable } } : s
                                            );
                                            setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                          }}
                                          className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                        >
                                          {variable}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {item.type === 'document' && item.content?.url && (
                                  <input
                                    type="text"
                                    placeholder="Nome do arquivo (opcional)"
                                    value={item.content?.fileName ?? ''}
                                    onChange={(e) => {
                                      const newSeq = sequence.map((s: any, i: number) =>
                                        i === index ? { ...s, content: { ...s.content, fileName: e.target.value } } : s
                                      );
                                      setFormData((prev) => ({ ...prev, messageContent: { sequence: newSeq } }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-8 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
